import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Lock, Loader2, MessageCircle } from 'lucide-react';

/**
 * Shown over the work area (everything except Social) once the admin-configured idle time has passed.
 * The user is still logged in — chat, calls and navigation keep working — but must re-enter the password
 * to continue with loans, customers, reports, etc.
 */
export default function WorkAreaLock() {
  const { user, unlockWork, logout, idleTimeoutMinutes } = useAuth();
  const navigate = useNavigate();
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const hours = Math.round((idleTimeoutMinutes || 0) / 60);

  const submit = async (e) => {
    e.preventDefault();
    if (!pw || busy) return;
    setBusy(true); setErr('');
    try { await unlockWork(pw); setPw(''); } catch (ex) { setErr(ex.message || 'Incorrect password'); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-forest-950/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="work-area-lock">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6 space-y-4 text-center">
        <div className="mx-auto w-12 h-12 rounded-full bg-forest-100 text-forest-700 flex items-center justify-center"><Lock className="w-6 h-6" /></div>
        <div>
          <h2 className="text-lg font-semibold">Work area locked</h2>
          <p className="text-sm text-muted-foreground mt-1">
            You have been inactive for {hours >= 1 ? `${hours} hour${hours > 1 ? 's' : ''}` : 'a while'}. Enter your password to continue, {user?.name?.split(' ')[0] || 'there'}.
            You are still logged in — Chat, Calls and Navigation keep working.
          </p>
        </div>
        <div className="rounded-lg bg-muted/60 p-3 text-left space-y-1">
          <label htmlFor="work-lock-password" className="text-xs font-medium">Password</label>
          <Input id="work-lock-password" type="password" autoFocus value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Your BBC password" className="h-11 bg-background" data-testid="work-lock-password" />
          {err && <p className="text-xs text-red-600" data-testid="work-lock-error">{err}</p>}
        </div>
        <Button type="submit" disabled={!pw || busy} className="w-full h-11 bg-forest-700 hover:bg-forest-800 text-white" data-testid="work-lock-submit">
          {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Unlock
        </Button>
        <div className="flex gap-2">
          <Button type="button" variant="outline" className="flex-1 h-10" onClick={() => navigate('/staff/chat')} data-testid="work-lock-go-social"><MessageCircle className="w-4 h-4 mr-1.5" />Go to Social</Button>
          <Button type="button" variant="ghost" className="flex-1 h-10 text-muted-foreground" onClick={() => logout()} data-testid="work-lock-logout">Log out</Button>
        </div>
      </form>
    </div>
  );
}
